<template>
  <main id="sk-main" class="sk-home__nuxt">
    <CustomSliderTopHome v-if="listing_top_data && listing_top_data.length > 3" :data="listing_top_data" />
    <HomeBannerSlider :language="language" :currencySetting="currencySetting" :pageSlug="pageSlug" :first-banner="firstBanner" />
    
    <SkeletonOurCollection v-if="!data_collection" />
    <LazyVisibleHomeOurCollection v-else :data="data_collection" :hydrate-on-visible="{ rootMargin: '100px' }" />

    <SkeletonSBCats v-if="!shop_by_cats" />
    <LazyVisibleHomeCustomShopCategory v-else :data="shop_by_cats" hydrate-on-visible />
    
    <SkeletonSBGifts v-if="!shop_by_gifts" />
    <LazyVisibleHomeShopByGifts v-else :data="shop_by_gifts" hydrate-on-visible />
    
    <LazyVisibleHomeGetTheLook :pageSlug="pageSlug" hydrate-on-visible />

    <SkeletonDelivery v-if="delivery_care === undefined" />
    <LazyVisibleHomeDelivery v-else :data="delivery_care" hydrate-on-visible />

    <LazyVisibleHomeSocials hydrate-on-visible />

    <SkeletonFaqs v-if="!faqs" />
    <LazyVisibleHomeFaqs v-else :data="faqs" hydrate-on-visible />
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import SkeletonOurCollection from "~/components/Home/Skeleton/SkeletonOurCollection.vue";
import SkeletonSBCats from "~/components/Home/Skeleton/SkeletonSBCats.vue";
import SkeletonSBGifts from "~/components/Home/Skeleton/SkeletonSBGifts.vue";
import SkeletonDelivery from "~/components/Home/Skeleton/SkeletonDelivery.vue";
import SkeletonFaqs from "~/components/Home/Skeleton/SkeletonFaqs.vue";
import CustomSliderTopHome from "~/components/Home/CustomSliderTopHome.vue";
import { useInsiderSdk } from '~/composables/insiderSdk';
// import jsonCurrencySettings from '~/resources/currency-settings.json';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
const { $ajax } = useNuxtApp();

const runtimeConfig = useRuntimeConfig();
const { $file } = useNuxtApp();
const homeData = ref();
interface IHomeResponse {
  success: boolean;
  data: string;
}

const language = "en";
const shop_by_cats = ref();
const shop_by_gifts = ref();
const custom_made_gifs = ref();
const delivery_care = ref();
const perfect_gifts = ref();
const faqs = ref();
const home_review = ref();
const dotScript = ref();
const new_letter_data = ref();
let mouseenterTouch = ref(false);
const userStore = useUserStore();
const route = useRoute();
const custom_our_service = ref();
const listing_top_data = ref();
const data_collection = ref();
const customer_review = ref();

const props = defineProps(["currencySetting", "pageSlug"]);
const { locale } = useI18n();
const heroLanguage = computed(() => locale.value === 'en' ? '' : locale.value);

// Start this first and independently of the larger home-page JSON request.
// It is awaited below, but both requests are already in flight in parallel.
const firstBannerRequest = useFetch<{ success: boolean; item: any }>('/api/home-banner-first', {
  key: `home-first-banner-${locale.value}`,
  query: heroLanguage.value ? { lang: heroLanguage.value } : undefined,
  server: true,
  lazy: false,
});
const firstBanner = ref<any>(null);

// Emit the LCP preload from the page itself. This runs as part of the home
// page's SSR pass, rather than waiting for a nested banner component to mount.
// `media` ensures that a mobile visit only starts the mobile poster request.
useHead(() => {
  const banner = firstBanner.value;
  if (!banner) return {};

  const pcPoster = banner.pc_video?.full || banner.img_pc?.full || banner.pc_video?.lazy || banner.img_pc?.lazy;
  const mbPoster = banner.mb_video?.full || banner.mb_image?.full || banner.mb_video?.lazy || banner.mb_image?.lazy;
  const link: Array<Record<string, string | number>> = [];

  if (pcPoster) {
    link.push({
      key: 'first-banner-pc',
      tagPriority: -1,
      rel: 'preload',
      as: 'image',
      href: pcPoster,
      media: '(min-width: 1000px)',
      fetchpriority: 'high',
    });
  }
  if (mbPoster) {
    link.push({
      key: 'first-banner-mb',
      tagPriority: -1,
      rel: 'preload',
      as: 'image',
      href: mbPoster,
      media: '(max-width: 999px)',
      fetchpriority: 'high',
    });
  }

  return { link };
});

const get_home_page_data = async () => {
  try {
      let fileName: string = 'sksg_home.json';
      if( typeof props.pageSlug !== 'undefined' ) {
          fileName = `${props.pageSlug}_${fileName}`;
      }
      // const response = await $file.get(`home/${fileName}`, {
      //   headers: {
      //     Authorization: `Bearer ${userStore.csrf}`,
      //     'X-API-Server': userStore.csrf_server
      //   },
      // });

      const { data: responseData, error } = await useAsyncData(
      'homepage',
        async () => {
          return $file.get(`home/${fileName}`, {
            headers: {
              Authorization: `Bearer ${userStore.csrf}`,
              'X-API-Server': userStore.csrf_server
            },
          });
        }
      )
      const response = responseData.value;

    if (response.success == true) {
      if (typeof response.data == "object") {
        homeData.value = response.data;
      } else {
        homeData.value = JSON.parse(response.data);
      }
      if (homeData.value.shop_by_cat) {
        shop_by_cats.value = homeData.value.shop_by_cat;
      }
      // if (homeData.value.custom_made_gifts) {
      //   custom_made_gifs.value = homeData.value.custom_made_gifts;
      // }
      if (homeData.value.form_script) {
        new_letter_data.value = homeData.value.form_script;
      }
      if (homeData.value.shop_by_gifts) {
        shop_by_gifts.value = homeData.value.shop_by_gifts;
      }
      if (homeData.value.delivery) {
        delivery_care.value = homeData.value.delivery;
      }
      if (homeData.value.our_service) {
        custom_our_service.value = homeData.value.our_service;
      }
      if (homeData.value.perfect_gifts) {
        perfect_gifts.value = homeData.value.perfect_gifts;
      }
      if (homeData.value.faqs) {
        faqs.value = homeData.value.faqs;
      }
      if (homeData.value.reviews) {
        home_review.value = homeData.value.reviews;
      }
      // if (homeData.value.customer_review) {
      //   customer_review.value = homeData.value.customer_review;
      // }
      if (homeData.value.dot_script) {
        dotScript.value = htmlSpecialcharsDecode(homeData.value.dot_script);
      }
    }
  } catch (error) {
    console.error("Error fetching campaign data:", error);
  }
};

function dotFormShow() {
  setTimeout(() => {
    if (mouseenterTouch.value === false) {
      var scriptElement = document.createElement('script');
      scriptElement.src = dotScript.value;

      var mainContent = document.querySelector('.sk-home__nuxt');
      if (mainContent) {

        mainContent.appendChild(scriptElement);
      }
      mouseenterTouch.value = true;
    }
  }, 3000);

}



const setPositionCookie = () => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  window.addEventListener('scroll', () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      localStorage.setItem('scrollPosition', window.scrollY.toString());
    }, 200);
  }, { passive: true });
}
const setScroll= ()=>{
    const scrollY = localStorage.getItem("scrollPosition") || 0;
    // console.log("🚀 ~ setScroll-home ~ scrollY:", scrollY)
    // if( localStorage.getItem("clicked") ) {
    //   console.log('scroll');
    //   localStorage.removeItem('clicked');
    // }
    // window.scroll({
    //     top: +scrollY||0,
    //     behavior:"instant"
    // })


}
  //set SEO homepage
  // Both payloads are kicked off before either is awaited. They are served by
  // Nitro from the local file cache, rather than making a loopback HTTP call.
  const homePageRequest = useFetch<IHomeResponse>('/api/home-data', {
    key: `homepage-${locale.value}`,
    query: heroLanguage.value ? { lang: heroLanguage.value } : undefined,
    server: true,
    lazy: false,
  });
  const { data: responseData } = await homePageRequest;
  const { data: firstBannerData } = await firstBannerRequest;
  firstBanner.value = firstBannerData.value?.item || null;

  let response: any = responseData.value;
  const legacyHomeRequest = !response?.success
    ? useAsyncData(`homepage-file-${locale.value}`, () => {
      let fileName = 'sksg_home.json';
      if (heroLanguage.value) fileName = `${heroLanguage.value}_${fileName}`;
      return $file.get(`home/${fileName}`, {
        headers: {
          Authorization: `Bearer ${userStore.csrf}`,
          'X-API-Server': userStore.csrf_server,
        },
      });
    })
    : null;

  const legacyBannerRequest = !firstBanner.value
    ? useAsyncData(`first-banner-file-${locale.value}`, () => {
      let fileName = 'sksg_home_first.json';
      if (heroLanguage.value) fileName = `${heroLanguage.value}_${fileName}`;
      return $file.get(`home/${fileName}`, {
        headers: {
          Authorization: `Bearer ${userStore.csrf}`,
          'X-API-Server': userStore.csrf_server,
        },
      });
    })
    : null;

  if (legacyHomeRequest) {
    const { data: legacyData } = await legacyHomeRequest;
    response = legacyData.value;
  }
  if (legacyBannerRequest) {
    const { data: legacyBannerData } = await legacyBannerRequest;
    firstBanner.value = legacyBannerData.value?.items?.[0] || null;
  }

  if (response?.success) {
    homeData.value = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    custom_our_service.value = homeData.value.our_service || null;
    listing_top_data.value = homeData.value.listing_item_top_home || null;
    shop_by_cats.value = homeData.value.shop_by_cat || null;
    // custom_made_gifs.value = homeData.value.custom_made_gifts || null;
    shop_by_gifts.value = homeData.value.shop_by_gifts || null;
    delivery_care.value = homeData.value.delivery || null;
    perfect_gifts.value = homeData.value.perfect_gifts || null;
    faqs.value = homeData.value.faqs || null;
    data_collection.value = homeData.value.collection || null;
    home_review.value = homeData.value.reviews || null;
    // customer_review.value = homeData.value.customer_review || null;
    dotScript.value = homeData.value.dot_script ? htmlSpecialcharsDecode(homeData.value.dot_script) : null;
  }
// get_home_page_data();


onMounted(async () => {
  // await setScroll();
  // window.addEventListener('popstate', setScroll);
  // window.addEventListener('pageshow', (event) => {
  //   if (event.persisted) {
  //     setScroll();
  //   }
  // });
  setPositionCookie();
  const element = document.getElementById('__nuxt');
  if (element && runtimeConfig.public.area == "SG") {
    element.addEventListener('mouseenter', dotFormShow);
    element.addEventListener('touchstart', dotFormShow);
  };

});

useHead({
  link: [
    { rel: 'alternate', hreflang: 'en-MY', href: 'https://www.skjewellery.com.my/' },
    { rel: 'alternate', hreflang: 'ms-MY', href: 'https://www.skjewellery.com.my/ms-my' },
    { rel: 'alternate', hreflang: 'en-SG', href: 'https://www.skjewellery.com/' },
    { rel: 'alternate', hreflang: 'en-US', href: 'https://www.skjewellery.com/us/' },
    { rel: 'alternate', hreflang: 'en-AU', href: 'https://www.skjewellery.com/au/' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://www.skjewellery.com/intl/' },
  ]
});

// insider sdk
  const {
    trackInsiderSdkEvent
    } = useInsiderSdk();
    trackInsiderSdkEvent(
          'insider_sdk_home',
          '',
          '',
      )
  // end inside sdk
  
</script>
